import { autoinject, transient } from "aurelia-dependency-injection";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { FindingRiskClassificationEnum, FindingRiskLevelEnum, RightShipInspectionNonConformityModel } from "../../../../../../models/rightship-inspections/rightship-inspections.models";
import { UnitOfWorkAction, UnitOfWorkObserver } from "../../../../../../utilities/unit-of-work-observer";
import { BindingEngine, computedFrom } from "aurelia-binding";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { RightShipInspectionsService } from "../../../../../../services/rightship-inspections/rightship-inspections.service";
import { Reflection } from "../../../../../../utilities/reflection";

@autoinject()
@transient()
export class GradeFindingDialog
{
    private _uow: UnitOfWorkObserver<RightShipInspectionNonConformityModel>;

    finding: RightShipInspectionNonConformityModel;
    currentFindingCausations: { [index: number]: object } = {};

    onSave: () => void
    viewOnly: boolean;

    riskLevels: { id: number, name: string, isSelected: boolean}[] = [
        { id: FindingRiskLevelEnum.High, name: "High", isSelected: false },
        { id: FindingRiskLevelEnum.Medium, name: "Medium", isSelected: false },
        { id: FindingRiskLevelEnum.Low, name: "Low", isSelected: false }
    ];

    riskClassifications: { id: number, name: string, isSelected: boolean}[] = [
        { id: FindingRiskClassificationEnum.Regulatory, name: "Regulatory", isSelected: false },
        { id: FindingRiskClassificationEnum.IndustryStandard, name: "Industry standard", isSelected: false }
    ];

    @computedFrom("finding.riskLevelId", "finding.riskClassificationId")
    public get hasChanges(): boolean{
        return this._uow.hasChanges();
    }

    public findingCausations: any[];

    constructor(private dialogService: DialogClientService, private bindingEngine: BindingEngine, private notificationService: NotificationClientService,
        private rightshipInspectionService: RightShipInspectionsService)
    {}

    activate(data: { finding: RightShipInspectionNonConformityModel, onSave: () => void, viewOnly: boolean})
    {
        if(data)
        {
            this.onSave = data.onSave;
            this.finding = CloneHelper.cloneObject(data.finding);

            this.startTracking();

            const riskLevelIndex = this.riskLevels.findIndex(x => x.id == (this.finding.riskLevelId ?? this.finding.defaultRiskLevelId));
            if(riskLevelIndex > -1)
            {
                this.riskLevels[riskLevelIndex].isSelected = true;
                this.finding.riskLevelId = this.finding.riskLevelId ?? this.riskLevels[riskLevelIndex].id;
            }
            const riskClassificationIndex = this.riskClassifications.findIndex(x => x.id == (this.finding.riskClassificationId ?? this.finding.defaultRiskClassificationId));
            if(riskClassificationIndex > -1)
            {
                this.riskClassifications[riskClassificationIndex].isSelected = true;
                this.finding.riskClassificationId = this.finding.riskClassificationId ?? this.riskClassifications[riskClassificationIndex].id;
            }
            this.viewOnly = data.viewOnly;
            
            if (!this.finding.findingCausations || this.finding.findingCausations.length == 0) {
                this.finding.findingCausations = [{ findingCausationL1Id: null, findingCauseOfConcernId: null, findingCausationL2Id: null, findingCausationL3Id: null, 
                    findingCausationL4Id: null, findingCausationL5Id: null, findingCausationL6Id: null, 
                    rightShipInspectionDeficiencyId: this.finding.rightShipInspectionDeficiencyId, isActive: true}];
            }
            
            this.initFindingCausations();
            
            this.findingCausations = this.finding.findingCausations;

        }
    }

    startTracking()
    {
        //initialize before staring tracking
        this.finding.isGradingConfirmed = this.finding.isGradingConfirmed ?? false;
        this.finding.riskLevelId = this.finding.riskLevelId ?? null;
        this.finding.riskClassificationId = this.finding.riskClassificationId ?? null;    
        this.finding.isSaveForLater = this.finding.isSaveForLater ?? false;

        this._uow = new UnitOfWorkObserver<RightShipInspectionNonConformityModel>(this.bindingEngine, this.finding, [Reflection.getPropertyName<RightShipInspectionNonConformityModel>(x => x.findingCausations)]);
        this._uow.startTracking();
    }

    riskLevelChanged()
    {
        const selectedRiskLevel = this.riskLevels.filter(x => x.isSelected);
        this.finding.riskLevelId = selectedRiskLevel.length > 0? selectedRiskLevel[0].id: null;
    }
    
    riskClassificationChanged()
    {
        const selectedRiskClassifications = this.riskClassifications.filter(x => x.isSelected);
        this.finding.riskClassificationId = selectedRiskClassifications.length > 0? selectedRiskClassifications[0].id: null;
    }

    close()
    {
        this.dialogService.hide(true);
    }

    
    deleteCausation(causation: any) {
        causation.isActive = false;
        this.findingCausations = this.finding.findingCausations.filter(x=>x.isActive);
    }

    addCausation(){
        this.finding.findingCausations.push({ findingCausationL1Id: null, findingCauseOfConcernId: null, findingCausationL2Id: null, findingCausationL3Id: null, 
            findingCausationL4Id: null, findingCausationL5Id: null, findingCausationL6Id: null, 
            rightShipInspectionDeficiencyId: this.finding.rightShipInspectionDeficiencyId, isActive: true});
        this.findingCausations = this.finding.findingCausations.filter(x=>x.isActive);
        const index = this.finding.findingCausations.length - 1;
        this.currentFindingCausations[index] = {...this.finding.findingCausations[index]};
    }

    changeFindingCausationL1Id(causation) {
        const findingIndex = this.finding.findingCausations.indexOf(causation);
        if (causation.findingCausationL2Id != null && causation.findingCausationL1Id != this.currentFindingCausations[findingIndex]['findingCausationL1Id']) {
            this.refreshCausationTreeFromLevel(1, findingIndex);
        }
        this.currentFindingCausations[findingIndex]['findingCausationL1Id'] = causation.findingCausationL1Id;
    }

    changeFindingCausationL2Id(causation) {
        const findingIndex = this.finding.findingCausations.indexOf(causation);
        if(causation.findingCausationL3Id != null && causation.findingCausationL2Id != this.currentFindingCausations[findingIndex]['findingCausationL2Id']){
            this.refreshCausationTreeFromLevel(2, findingIndex);
        }
        this.currentFindingCausations[findingIndex]['findingCausationL2Id'] = causation.findingCausationL2Id;
    }

    changeFindingCausationL3Id(causation) {
        const findingIndex = this.finding.findingCausations.indexOf(causation);
        if(causation.findingCausationL4Id != null && causation.findingCausationL3Id != this.currentFindingCausations[findingIndex]['findingCausationL3Id']){
            this.refreshCausationTreeFromLevel(3, findingIndex);
        }
        this.currentFindingCausations[findingIndex]['findingCausationL3Id'] = causation.findingCausationL3Id;
    }

    changeFindingCausationL4Id(causation) {
        const findingIndex = this.finding.findingCausations.indexOf(causation);
        if(causation.findingCausationL5Id != null && causation.findingCausationL4Id != this.currentFindingCausations[findingIndex]['findingCausationL4Id']){
            this.refreshCausationTreeFromLevel(4, findingIndex);
        }
        this.currentFindingCausations[findingIndex]['findingCausationL4Id'] = causation.findingCausationL4Id;
    }

    changeFindingCausationL5Id(causation) {
        const findingIndex = this.finding.findingCausations.indexOf(causation);
        if(causation.findingCausationL6Id != null && causation.findingCausationL5Id != this.currentFindingCausations[findingIndex]['findingCausationL5Id']){
            this.refreshCausationTreeFromLevel(5, findingIndex);
        }
        this.currentFindingCausations[findingIndex]['findingCausationL5Id'] = causation.findingCausationL5Id;
    }

    refreshCausationTreeFromLevel(fromLevel: number, findingIndex: number) {
        const currentCausation = this.findingCausations[findingIndex];
        switch (fromLevel) {
            case 1:
                currentCausation.findingCausationL2Id = null;
                break;
            case 2:
                currentCausation.findingCausationL3Id = null;
                break;
            case 3:
                currentCausation.findingCausationL4Id = null;
                break;
            case 4:
                currentCausation.findingCausationL5Id = null;
                break;
            case 5: 
                currentCausation.findingCausationL6Id = null;
                break;
            default:
                break;
        }
    }

    initFindingCausations() {
        this.finding.findingCausations.forEach((fc, index) => {
            // Use spread operator to avoid shallow copy
            this.currentFindingCausations[index] = {...fc};
        }); 
    }

    async save(isFinalise: boolean)
    {   
        try
        {            
            const changes = this._uow.getChanges();
            
            if(isFinalise) {
                changes["isGradingConfirmed"] = {
                    "action": UnitOfWorkAction.Modified,
                    "oldValue": this.finding.isGradingConfirmed,
                    "value": true
                }
            }
            else {
                changes["isGradingConfirmed"] = {
                    "action": UnitOfWorkAction.Modified,
                    "oldValue": this.finding.isGradingConfirmed,
                    "value": false
                }
                changes["isSaveForLater"] = {
                    "action": UnitOfWorkAction.Modified,
                    "oldValue": this.finding.isSaveForLater,
                    "value": true
                }
            }
            await this.rightshipInspectionService.saveFindingGrading({ id: this.finding.rightShipInspectionDeficiencyId, changes: changes, 
                                                                    findingCausations: this.finding.findingCausations});
            
            if(this.onSave) 
                this.onSave();

            this.notificationService.show("Saved", "Finding grading updated successfully.", NotificationType.Success);

            this.dialogService.hide(true);
        }
        catch (e)
        {
            this.notificationService.show("Failed", "Failed to update finding grading.", NotificationType.Error);
            console.error(e);
        }
        
    }
}